import { Individual } from "@/Common/Api/Individuals/Dto/Individual";
import { LoadIndividualResponse } from "@/Common/Api/Individuals/Dto/LoadIndividualResponse";
import { ContactFacetType } from "@/Common/Models/Individuals/Enums/ContactFacetType";
import * as LocalOperations from "@/Common/Operations/operations.local";
import * as ServerOperations from "@/Common/Operations/operations.server";
import * as SubServerOperations from "@/Common/Operations/operations.server.sub";
import { ServerEvent, ServerEventWithThen } from "@/Common/Stores/Actions/ServerEvent";
import { BaseServerOperationHandler } from "@/Common/Stores/Handlers/Server/BaseServerOperationHandler";
import { DateOnly } from "@/Common/Utilities/DateAndTime/DateOnly";
import { ConvertRelativeToPatientResponse } from "../Api/Dto/ConvertRelativeToPatientResponse";
import { Relative } from "../Models/Relative";
import { RelativeActions } from "./RelativeActions";
import { RelativeStore } from "./RelativeStore";

class RelativeResponseHandler extends BaseServerOperationHandler {
    constructor(action: RelativeActions) {
        super(action);
        this.RegisterOperation(ServerOperations.Server.Myle_LoadIndividual, this.loadRelativeAndRedirect, SubServerOperations.Relative.LoadAndOpenFile, false);
        this.RegisterOperation(ServerOperations.Server.Myle_Individual_Demographics_Update, this.updateDemographics, null, true);
        this.RegisterOperation(ServerOperations.Server.Myle_Individual_Contacts_Update, this.updateContacts, null, true);
        this.RegisterOperation(ServerOperations.Server.Myle_Individual_Identifiers_Update, this.updateIdentifiers, null, true);
        this.RegisterOperation(ServerOperations.Server.Myle_ConvertRelativeToPatient, this.convertRelativeToPatientAndReload, SubServerOperations.Relative.LoadAndOpenFile);
        this.RegisterOperation(ServerOperations.Server.Myle_Individual_Relationships_Update, this.relationshipsUpdate, null, true);
        this.RegisterOperation(ServerOperations.Server.Myle_Patient_Memo_Save, this.memoUpdate, null, true);
        this.RegisterOperation(ServerOperations.Server.Myle_Patient_Deactivate, this.deactivateRelative, null, true);
        this.RegisterOperation(ServerOperations.Server.Myle_Patient_Reactivate, this.reactivateRelative, null, true);
    }

    private loadRelativeAndRedirect(response: ServerEventWithThen<any, () => void>, store: RelativeStore): void {
        const relative = Relative.fromLoadIndividual(response.Data as LoadIndividualResponse) as Relative;
        store.relatives[relative.ContactId] = relative;
        sessionStore.dispatch(LocalOperations.Person.OpenFile, { person: relative });
    }

    private updateDemographics(response: ServerEvent, store: RelativeStore): void {
        const updatedIndividual: Individual = response.Data.Individual;
        const file = store.relativeFiles[updatedIndividual.Contact_id];
        const relative = store.relatives[updatedIndividual.Contact_id];

        if (relative) {
            relative.PrefixLookupId = updatedIndividual.PrefixLookupId;
            relative.FirstName = updatedIndividual.FirstName;
            relative.LastName = updatedIndividual.LastName;
            relative.PreferredName = updatedIndividual.PreferredName;
            relative.PreferredPronouns = updatedIndividual.PreferredPronouns;
            relative.SpecifiedPreferredPronouns = updatedIndividual.SpecifiedPreferredPronouns;
            relative.GenderIdentity = updatedIndividual.GenderIdentity;
            relative.GenderLookupId = updatedIndividual.GenderLookupId;
            relative.DateOfBirth = updatedIndividual.DateOfBirth && new DateOnly(updatedIndividual.DateOfBirth).toJSDate();
            store.relatives[relative.ContactId] = new Relative(relative);
        }

        if (file) {
            file.isEditing = false;
            store.relativeFiles[store.selectedPersonContactId] = { ...file };
        }
    }

    private updateContacts(response: ServerEvent, store: RelativeStore): void {
        const updatedIndividual: Individual = response.Data.Individual;
        const file = store.relativeFiles[updatedIndividual.Contact_id];
        const relative = store.relatives[updatedIndividual.Contact_id];

        if (relative) {
            relative.ContactPoints = response.Data.ContactPoints;
            relative.Addresses = response.Data.Addresses;
            relative.Phones = response.Data.PhoneNumbers;
            relative.Emails = response.Data.Emails;
            relative.DoNotEmail = response.Data.DoNotEmail;
            store.relatives[relative.ContactId] = new Relative(relative);
        }

        if (file) {
            file.isEditing = false;
            store.relativeFiles[store.selectedPersonContactId] = { ...file };
        }
    }

    private updateIdentifiers(response: ServerEvent, store: RelativeStore): void {
        const updatedIndividual: Individual = response.Data.Individual;
        const file = store.relativeFiles[updatedIndividual.Contact_id];
        const relative = store.relatives[updatedIndividual.Contact_id];

        if (relative) {
            if (updatedIndividual.DateOfBirth)
                relative.DateOfBirth = new Date(updatedIndividual.DateOfBirth);

            relative.NAM = updatedIndividual.NAM;
            relative.HealthCards = [...response.Data.HealthCards];
            relative.OtherPatientIdentifiers = [...response.Data.OtherIdentifiers];

            relative.HealthCards?.forEach(healthCard => {
                if (healthCard.ProvinceLookupId) {
                    healthCard.CountryLookupId = cacheStore.GetCountryForRegion(healthCard.ProvinceLookupId).LookupID;
                }
            });

            store.relatives[relative.ContactId] = new Relative(relative);
        }

        if (file) {
            file.isEditing = false;
            store.relativeFiles[store.selectedPersonContactId] = { ...file };
        }
    }

    private convertRelativeToPatientAndReload(response: ServerEvent, store: RelativeStore): void {
        const convertRelativeToPatientResponse = response.Data as ConvertRelativeToPatientResponse;
        const contactId = convertRelativeToPatientResponse.PatientContactId;

        // Clean store
        delete store.relatives[contactId];
        delete store.relativeFiles[contactId];
        store.selectedPersonContactId = null;

        // Open patient file
        sessionStore.dispatch(LocalOperations.Person.LoadFile, { contactId: contactId, contactFacetType: ContactFacetType.Patient }, 0);
    }

    private relationshipsUpdate(response: ServerEvent, store: RelativeStore): void {
        const relationships = response.Data["Relationships"];
        const file = store.GetCurrentFile();
        const relative = store.GetCurrent();

        if (response.Errors != null && response.Errors.length > 0) {
            sessionStore.showPopup(response.Errors);
            file.isEditing = true;
            store.relativeFiles[store.selectedPersonContactId] = { ...file };
            return;
        }

        if (relative) {
            relative.Relationships = relationships;
            store.relatives[relative.ContactId] = new Relative(relative);
        }

        if (file) {
            file.isEditing = false;
            store.relativeFiles[store.selectedPersonContactId] = { ...file };
        }
    }

    private memoUpdate(response: ServerEvent, store: RelativeStore): void {
        const individual: Individual = response.Data.Individual;
        const relative = store.relatives[individual.Contact_id];

        if (relative) {
            relative.memoLastModifiedDate = individual.MemoLastModifiedDate;
            relative.memoNote = individual.MemoNote;
            relative.lastModifiedMemoEmployeeId = individual.LastModifiedMemoEmployeeId;
            store.relatives[relative.ContactId] = new Relative(relative);
        }
    }

    private deactivateRelative(response: ServerEvent, store: RelativeStore): void {
        const relative = store.relatives[response.Data.PatientContactId];

        if (relative) {
            relative.inactivationNote = response.Data.DeactivationNote;
            relative.isInactive = true;
            relative.reasonForInactivityLookupId = response.Data.ReasonForInactivityLookupId;
            store.relatives[relative.ContactId] = new Relative(relative);
        }
    }

    private reactivateRelative(response: ServerEvent, store: RelativeStore): void {
        const relative = store.relatives[response.Data.PatientContactId];

        if (relative) {
            relative.inactivationNote = "";
            relative.isInactive = false;
            relative.reasonForInactivityLookupId = null;
            store.relatives[relative.ContactId] = new Relative(relative);
        }
    }
}

export { RelativeResponseHandler };
